import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useRouter } from 'next/router';
import { TFunction, useTranslation } from 'next-i18next';
import { FC, MouseEvent, useCallback, useMemo, useState } from 'react';

import { ProductWithPrices } from '@commerce/types/product';
import ContentModal from '@components/common/ContentModal/ContentModal';
import RadioTab, { RadioTabProps } from '@components/common/RadioTabs/RadioTab/RadioTab';
import { PremiumUpsellContent } from '@components/common/types/PremiumUpsellContent';
import { PremiumSource, SubscriptionTerm, SubscriptionTier, SubscriptionType } from '@components/product/enums';
import { getPremiumMap, getPremiumSKU } from '@components/product/helpers';
import Text from '@components/ui/Text/Text';
import useAddItem from '@framework/cart/use-add-item';
import useRemoveItem from '@framework/cart/use-remove-item';
import { pushToGTMDataLayer } from '@lib/gtag';

import s from './PremiumUpsell.module.scss';

export enum UpsellValue {
  PREMIUM = 'premium',
  STANDARD = 'standard',
  // special case only for US and when protect + HW is in cart (rendered as upsell module)
  PROTECT = 'premium-protect',
  L360_GOLD = 'l360-gold',
}

interface Props {
  className?: string;
  selected?: {
    id: string;
    lineItemId: string;
    tier?: SubscriptionTier;
  } | null;
  subscriptions: ProductWithPrices[];
  onUpsellSelect?: () => void;
  onStandardSelect?: () => void;
  tabValue?: UpsellValue;
  upsellDetails?: Record<string, string> | null;
  premiumUpsellContent?: PremiumUpsellContent;
  // option to override upsell premium plan
  upsellPremium?: ProductWithPrices & { upsellValue: UpsellValue };
  hideStandardOption?: boolean;
  hidePrice?: boolean;
}

interface RadioProps extends Omit<RadioTabProps, 'label' | 'content'>, Pick<Props, 'hidePrice'> {
  loading: boolean;
  t: TFunction;
  premium?: ProductWithPrices;
  price?: string | null;
  subLabel?: string | null;
  premiumUpsellContent?: PremiumUpsellContent;
  onDetailsClick?: (e: MouseEvent) => void;
}

const PremiumUpsellRadio: FC<RadioProps> = ({
  premium,
  t,
  price,
  subLabel,
  onDetailsClick,
  premiumUpsellContent = {},
  hidePrice,
  ...radioProps
}) => {
  const {
    premiumSectionTitle = premium?.name,
    premiumSectionDescription = premium?.pdpDescription || premium?.description,
    premiumSectionFootnote = premium?.pdpFootnote,
    premiumSectionSublabel = subLabel,
    premiumSectionPriceText = price,
    premiumSectionButtonText = t('cart:upsell.details'),
    standardSectionTitle = t('cart:upsell.free.label'),
    standardSectionDescription = t('cart:upsell.free.description'),
    standardSectionPriceText = price,
    standardSectionButtonText = t('cart:upsell.details'),
  } = premiumUpsellContent;

  return (
    <>
      <RadioTab
        {...radioProps}
        data-cy={`upsell-${premium ? 'premium' : 'standard'}-option`}
        label={
          <>
            <Text
              className="mr-1 text-primary"
              variant="base-bold"
              data-cy={`upsell-${premium ? 'premium' : 'standard'}-label`}
              asElement="span"
            >
              {premium ? premiumSectionTitle : standardSectionTitle}
            </Text>
            {!!subLabel && (
              <Text variant="base-bold" className="text-primary" asElement="span">
                {premiumSectionSublabel}
              </Text>
            )}
          </>
        }
        content={
          <>
            <Text
              html={premium ? premiumSectionDescription : standardSectionDescription}
              variant="medium"
              className={cn(s.description, 'my-3')}
              data-testid={`upsell-${premium ? 'premium' : 'standard'}-description`}
            />
            <div className="mt-auto flex justify-between items-center">
              {!!price && !hidePrice && (
                <Text variant="base-bold" className="text-primary" asElement="span">
                  {premium ? premiumSectionPriceText : standardSectionPriceText}
                </Text>
              )}
              {!!onDetailsClick && (
                <button
                  type="button"
                  className={cn(s.detailsBtn, { 'mr-0 ml-auto': !price })}
                  onClick={onDetailsClick}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Text variant="medium" className="text-bone" asElement="span">
                    {premium ? premiumSectionButtonText : standardSectionButtonText}
                  </Text>
                </button>
              )}
            </div>
            {premium && premiumSectionFootnote && (
              <Text
                html={premiumSectionFootnote}
                className={cn(s.footnote, 'mt-3')}
                data-testid={`upsell-${premium ? 'premium' : 'standard'}-footnote`}
              />
            )}
          </>
        }
        allClickable
      />
    </>
  );
};

const PremiumUpsell: FC<Props> = ({
  className,
  selected,
  subscriptions,
  onStandardSelect: onStandardSelectProp,
  onUpsellSelect: onUpsellSelectProp,
  tabValue: valueProp,
  upsellDetails,
  premiumUpsellContent,
  upsellPremium: upsellPremiumProps,
  hideStandardOption = false,
  hidePrice = false,
}) => {
  const { locale } = useRouter();
  const { t } = useTranslation(['cart', 'common', 'product']);
  const addItem = useAddItem();
  const removeItem = useRemoveItem();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const selectedLineItemId = useMemo(() => selected?.lineItemId || null, [selected]);
  const tabValue = valueProp ?? (selectedLineItemId ? UpsellValue.PREMIUM : UpsellValue.STANDARD);
  const premiumMap = useMemo(() => getPremiumMap(locale), [locale]);
  const upsellValue = useMemo(
    () => upsellPremiumProps?.upsellValue || UpsellValue.PREMIUM,
    [upsellPremiumProps?.upsellValue]
  );

  const [modal, setModal] = useState<string | null>(null);

  const upsellPremium = useMemo(() => {
    if (upsellPremiumProps) {
      return upsellPremiumProps;
    }

    // get upsell premium tier
    const upsellSKU = getPremiumSKU(
      premiumMap,
      SubscriptionTier.PREMIUM,
      SubscriptionTerm.YEAR,
      SubscriptionType.UPSELL
    );
    return upsellSKU ? subscriptions.find(({ sku }) => sku === upsellSKU) : null;
  }, [subscriptions, premiumMap, upsellPremiumProps]);

  // just to grab the correct name from Jasper (without 'Tile') as duplicate product names are not allowed
  const basePremium = useMemo(() => {
    const baseSKU = getPremiumSKU(
      premiumMap,
      SubscriptionTier.PREMIUM,
      SubscriptionTerm.YEAR,
      SubscriptionType.STANDALONE
    );
    return baseSKU ? subscriptions.find(({ sku }) => sku === baseSKU) : null;
  }, [subscriptions, premiumMap]);

  const selectedPremium = useMemo(() => {
    return selected?.id ? subscriptions.find(({ id }) => id === selected.id) : null;
  }, [selected, subscriptions]);

  const isUpsell = !selectedPremium?.id || upsellPremium?.id === selectedPremium.id;

  const details =
    modal && upsellDetails?.[modal]
      ? {
          header:
            modal !== UpsellValue.STANDARD ? (selectedPremium || upsellPremium)?.name : t('cart:upsell.free.label'),
          body: upsellDetails[modal],
        }
      : null;

  const onUpsellSelect = useCallback(async () => {
    if (upsellPremium) {
      try {
        setLoading(true);
        const updated = await addItem({
          productId: upsellPremium.id,
          sku: upsellPremium.sku,
          productName: upsellPremium.name,
          premiumSource: PremiumSource.CART_UPSELL,
        });
        if (!updated?.cart) {
          setError(t('cart:error.upsell', { method: t('cart:common.add') }));
        }
        pushToGTMDataLayer({ event: 'upsellPremium', source: 'cart' });
      } finally {
        setLoading(false);
      }
    }
  }, [upsellPremium, addItem, t]);

  // `Tile Standard` a.k.a no premium
  const onStandardSelect = useCallback(async () => {
    if (selectedLineItemId) {
      try {
        setLoading(true);
        const updated = await removeItem({
          id: selectedLineItemId,
          productId: upsellPremium?.id,
          variant: { sku: upsellPremium?.sku },
          quantity: 1,
        });
        if (!updated) {
          setError(t('cart:error.upsell', { method: t('cart:common.remove') }));
        }
        pushToGTMDataLayer({ event: 'upsellStandard', source: 'cart' });
      } finally {
        setLoading(false);
      }
    }
  }, [selectedLineItemId, removeItem, upsellPremium, t]);

  const containerClass = cn(s.item, {
    'opacity-60 pointer-events-none cursor-wait': loading,
  });

  const onUpsellDetailsClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      if (isUpsell) {
        setModal(upsellValue);
      }

      if (selected?.tier === SubscriptionTier.PROTECT) {
        setModal(UpsellValue.PROTECT);
      }
    },
    [isUpsell, selected?.tier, upsellValue]
  );

  return upsellPremium ? (
    <>
      <div className={cn(s.wrapper, className)}>
        <PremiumUpsellRadio
          loading={loading}
          // if upsell premium, get the name from base premium
          premium={isUpsell ? { ...upsellPremium, name: basePremium?.name || upsellPremium.name } : selectedPremium}
          t={t}
          price={isUpsell ? upsellPremium.upsellPrice : null}
          subLabel={isUpsell ? upsellPremium.upsellTitle : null}
          value={tabValue}
          itemValue={upsellValue}
          onChange={onUpsellSelectProp || onUpsellSelect}
          group="premiumUpsell"
          containerClass={containerClass}
          activeContainerClass={s.itemActive}
          alwaysShowContent
          onDetailsClick={onUpsellDetailsClick}
          hideRadio
          hidePrice={hidePrice}
          premiumUpsellContent={premiumUpsellContent}
        />

        {!hideStandardOption && (
          <PremiumUpsellRadio
            loading={loading}
            t={t}
            price={t('cart:upsell.included')}
            value={tabValue}
            itemValue={UpsellValue.STANDARD}
            onChange={onStandardSelectProp || onStandardSelect}
            group="premiumUpsell"
            containerClass={containerClass}
            activeContainerClass={s.itemActive}
            alwaysShowContent
            onDetailsClick={(e: MouseEvent) => {
              e.stopPropagation();
              setModal(UpsellValue.STANDARD);
            }}
            hideRadio
            hidePrice={hidePrice}
            premiumUpsellContent={premiumUpsellContent}
          />
        )}
      </div>
      {error && (
        <Text className="mt-1.5" color="var(--error)">
          {error}
        </Text>
      )}
      {!!upsellDetails && !isEmpty(upsellDetails) && (
        <ContentModal
          details={details?.body}
          header={details?.header}
          open={!!modal}
          onClose={() => setModal(null)}
          contentSpacing="gap"
        />
      )}
    </>
  ) : null;
};

export default PremiumUpsell;
